import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../../context/ThemeContext";

type ResponsiveWidths = {
  [breakpoint: number]: { [colIndex: number]: string };
};

type ZebraColors = { odd: string; even: string };

type FinanceStatus = "pending_payment" | "paid";
type FinanceFilter = "__all__" | "pending" | "paid";

type SortConfig = { index: number | null; direction: "asc" | "desc" };

type ChipKey =
  | "all"
  | "po"
  | "withpo"
  | "component"
  | "analysis"
  | "pickup"
  | "ready";

type OverlayState = {
  visible: boolean;
  x: number;
  y: number;
  text: string;
};

type TableProps = {
  selectedTextWhite?: boolean;
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  columnWidths?: string[];
  responsiveWidths?: ResponsiveWidths;
  rowIds?: Array<string | number>;

  onDoubleClick?: (
    viewIndex: number,
    rowData: (string | number | React.ReactNode)[],
    originalIndex?: number
  ) => void;

  filters?: string[];
  onFiltersChange?: (filters: string[]) => void;

  isTableStandby?: boolean;
  osColumnIndex?: number;

  enableCellColors?: boolean;
  getCellStyle?: (
    rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    originalIndex: number,
    rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => React.CSSProperties | undefined;

  getRowStyle?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => React.CSSProperties | undefined;

  enableZebra?: boolean;
  zebraColors?: ZebraColors;
  hoverHighlight?: boolean;
  dense?: boolean;
  softBorders?: boolean;
  stickyFirstCol?: boolean;

  atrasoColumnIndex?: number;
  atrasoColors?: {
    good?: string;
    warn?: string;
    bad?: string;
  };

  isRowPending?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => boolean;

  delayScope?: "cell" | "row";

  getRowGroup?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => string | null;

  groupLabels?: { [groupKey: string]: string };

  companyColumnIndex?: number;
  showFinanceFilter?: boolean;
};

const normalizeText = (v: any) =>
  (typeof v === "string" || typeof v === "number" ? String(v) : "")
    .trim()
    .toLowerCase();

const normalizeStatusText = (v: any) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normalizeName = (v: any) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const includesAny = (text: string, terms: string[]) =>
  terms.some((term) => text.includes(term));

function cellToPlainText(cell: any): string {
  if (cell === null || cell === undefined) return "";

  if (typeof cell === "string" || typeof cell === "number") {
    return String(cell);
  }

  if (Array.isArray(cell)) {
    return cell.map(cellToPlainText).join(" ").trim();
  }

  if (React.isValidElement(cell)) {
    const props: any = cell.props || {};

    const ownTitle =
      typeof props.title === "string" || typeof props["aria-label"] === "string"
        ? String(props.title || props["aria-label"])
        : "";

    const childrenText = cellToPlainText(props.children);

    return [ownTitle, childrenText].filter(Boolean).join(" ").trim();
  }

  if (typeof cell === "object") {
    try {
      return JSON.stringify(cell);
    } catch {
      return "";
    }
  }

  return "";
}

function splitNamesForOverlay(text: string): string[] {
  const raw = String(text || "").trim();
  if (!raw) return [];

  if (
    raw === "—" ||
    raw === "-" ||
    raw.toLowerCase() === "n/a" ||
    raw.toLowerCase() === "na"
  ) {
    return [];
  }

  return raw
    .split(/\s*,\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
}

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  columnWidths = [],
  responsiveWidths = {},
  rowIds,
  onDoubleClick,

  filters: externalFilters,
  onFiltersChange,

  isTableStandby = false,
  osColumnIndex = 0,

  enableCellColors = false,
  getCellStyle,
  getRowStyle,

  enableZebra = true,
  zebraColors = { odd: "#fbfbfc", even: "#ffffff" },
  hoverHighlight = true,
  dense = false,
  softBorders = true,
  stickyFirstCol = false,

  atrasoColumnIndex,
  atrasoColors,

  isRowPending,
  delayScope = "cell",

  getRowGroup,
  groupLabels,

  companyColumnIndex,
  showFinanceFilter = false,

  selectedTextWhite = false,
}) => {
  const [internalFilters, setInternalFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );

  const filters = externalFilters ?? internalFilters;

  const updateFilters = (nextFilters: string[]) => {
    if (onFiltersChange) {
      onFiltersChange(nextFilters);
      return;
    }
    setInternalFilters(nextFilters);
  };

  const [statusChip, setStatusChip] = useState<ChipKey>("all");
  const [companyFilter, setCompanyFilter] = useState<string>("__all__");
  const [personFilter, setPersonFilter] = useState<string>("__all__");
  const [financeFilter, setFinanceFilter] = useState<FinanceFilter>("__all__");
  const [financeMap, setFinanceMap] = useState<Record<number, FinanceStatus>>(
    {}
  );

  const [selectedViewIndex, setSelectedViewIndex] = useState<number | null>(
    null
  );
  const [selectedOS, setSelectedOS] = useState<string | null>(null);
  const [activeWidths, setActiveWidths] = useState<string[]>(columnWidths);
  const [fontSize, setFontSize] = useState("14px");
  const [hoverRow, setHoverRow] = useState<number | null>(null);

  const [sort, setSort] = useState<SortConfig>({
    index: null,
    direction: "asc",
  });

  const [overlay, setOverlay] = useState<OverlayState>({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });

  const tableRef = useRef<HTMLDivElement>(null);
  const overlayRafRef = useRef<number | null>(null);

  const { theme, departmentColor } = useTheme();

  const borderColor = softBorders ? "#d9dde3" : "#c7ccd4";
  const headerBg = "#f1f2f4";
  const headerText = "#454545";
  const filterBg = "#fafafa";
  const groupBarBg = "#eceef1";
  const groupBarText = "#7b8190";
  const footerBg = "#f6f7f9";
  const footerBorder = "#d5d9df";

  const baseSelectionColor = departmentColor || theme?.primary || "#0a343d";

  const optionSelected = `${baseSelectionColor}C0`;
  const optionHover = `${baseSelectionColor}33`;

  const PENDING_BASE = "#fff6cf";
  const PENDING_SELECTED_SOLID = "#e8ddb0";
  const ROW_HOVER_BG = optionHover;

  const atrasoGood = atrasoColors?.good ?? "#dff0df";
  const atrasoWarn = atrasoColors?.warn ?? "#f1d59d";
  const atrasoBad = atrasoColors?.bad ?? "#d66a6a";

  const FINANCE_COLORS: Record<FinanceStatus, string> = {
    pending_payment: "#F50B0B",
    paid: "#22C55E",
  };

  const headerHeight = dense ? "30px" : "34px";
  const filterHeight = dense ? "24px" : "28px";
  const cellHeight = dense ? "26px" : "28px";
  const cellPadding = dense ? "0 6px" : "0 8px";

  useEffect(() => {
    setInternalFilters((prev) => {
      if (prev.length === headers.length) return prev;
      return Array(headers.length).fill("");
    });
  }, [headers.length]);

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      const sorted = Object.keys(responsiveWidths)
        .map(Number)
        .sort((a, b) => b - a);
      const bp = sorted.find((x) => width <= x) ?? null;

      if (bp) {
        const conf = responsiveWidths[bp];
        const w = headers.map(
          (_, i) => conf[i + 1] || columnWidths[i] || "auto"
        );
        setActiveWidths(w);
      } else {
        setActiveWidths(columnWidths);
      }

      const clamped = Math.max(12, Math.min(18, width / 120));
      setFontSize(clamped + "px");
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [responsiveWidths, headers, columnWidths]);

  useEffect(() => {
    const defaultSortIndex = (() => {
      const exactCase = headers.findIndex((h) => normalizeText(h) === "case");
      if (exactCase >= 0) return exactCase;

      const containsCase = headers.findIndex((h) =>
        normalizeText(h).includes("case")
      );
      if (containsCase >= 0) return containsCase;

      return headers.length > 0 ? 0 : null;
    })();

    setSort((prev) => {
      if (prev.index !== null) return prev;
      return {
        index: defaultSortIndex,
        direction: "asc",
      };
    });
  }, [headers]);

  useEffect(() => {
    const nextFinance: Record<number, FinanceStatus> = {};
    rows.forEach((_, i) => {
      nextFinance[i] = "pending_payment";
    });
    setFinanceMap(nextFinance);
  }, [rows]);

  useEffect(() => {
    const hide = () =>
      setOverlay((prev) => (prev.visible ? { ...prev, visible: false } : prev));

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);

      if (overlayRafRef.current != null) {
        cancelAnimationFrame(overlayRafRef.current);
      }
    };
  }, []);

  const parseMaybeNumber = (v: string): number | null => {
    const n = Number(
      v
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".")
    );
    return Number.isFinite(n) ? n : null;
  };

  const extractOS = (cell: string | number | React.ReactNode): string => {
    if (typeof cell === "string" || typeof cell === "number") {
      return String(cell);
    }

    if (
      cell &&
      typeof cell === "object" &&
      "props" in (cell as any) &&
      (cell as any).props?.os
    ) {
      return String((cell as any).props.os);
    }

    return cellToPlainText(cell);
  };

  const getComparable = (cell: string | number | React.ReactNode) => {
    const asText = cellToPlainText(cell);

    if (typeof cell === "number") return { kind: "num" as const, val: cell };

    if (typeof cell === "string") {
      const maybe = parseMaybeNumber(cell);
      if (maybe !== null) return { kind: "num" as const, val: maybe };
      return { kind: "str" as const, val: cell };
    }

    const maybe = parseMaybeNumber(asText);
    if (maybe !== null) return { kind: "num" as const, val: maybe };

    return { kind: "str" as const, val: asText };
  };

  const rowBaseBg = (i: number) =>
    enableZebra
      ? i % 2 === 0
        ? zebraColors.odd
        : zebraColors.even
      : "#ffffff";

  const statusColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => normalizeText(h).includes("status"));
    return idx >= 0 ? idx : null;
  }, [headers]);

  const copiaColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => normalizeText(h) === "em cópia");
    return idx >= 0 ? idx : null;
  }, [headers]);

  const solicitanteColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => {
      const n = normalizeText(h);
      return (
        n.includes("solicitante") ||
        n.includes("responsável") ||
        n.includes("responsavel")
      );
    });
    return idx >= 0 ? idx : null;
  }, [headers]);

  const effectiveCompanyColIndex = useMemo(() => {
    if (
      typeof companyColumnIndex === "number" &&
      companyColumnIndex >= 0 &&
      companyColumnIndex < headers.length
    ) {
      return companyColumnIndex;
    }

    const idx = headers.findIndex((h) => normalizeText(h).includes("empresa"));
    return idx >= 0 ? idx : null;
  }, [companyColumnIndex, headers]);

  const firstVisibleColIndex = useMemo(() => {
    const idx = activeWidths.findIndex((w) => w !== "0px");
    return idx >= 0 ? idx : 0;
  }, [activeWidths]);

  const getStatusText = (cell: any) => cellToPlainText(cell);

  const statusKeyOf = (statusText: string): ChipKey | "other" => {
    const s = normalizeStatusText(statusText);

    if (
      includesAny(s, [
        "equipamento com p.o.",
        "equipamento com p.o",
        "equipamento com po",
        "com p.o.",
        "com p.o",
        "com po",
      ])
    ) {
      return "withpo";
    }

    if (
      includesAny(s, [
        "aguardando p.o.",
        "aguardando p.o",
        "aguardando po",
        "equipamento aguardando p.o.",
        "equipamento aguardando p.o",
        "equipamento aguardando po",
      ])
    ) {
      return "po";
    }

    if (
      includesAny(s, [
        "aguardando componente",
        "equipamento aguardando componente",
      ])
    ) {
      return "component";
    }

    if (
      includesAny(s, [
        "em analise",
        "em análise",
        "equipamento em analise",
        "equipamento em análise",
      ])
    ) {
      return "analysis";
    }

    if (
      includesAny(s, [
        "aguardando retirada",
        "aguardando retirada do equipamento",
      ])
    ) {
      return "pickup";
    }

    if (
      includesAny(s, [
        "equipamento reparado",
        "equipamento operacional",
        "reparado",
        "operacional",
      ])
    ) {
      return "ready";
    }

    return "other";
  };

  const statusStyle = (statusText: string) => {
    const key = statusKeyOf(statusText);

    if (key === "po") return { bg: "#FDE7C7", fg: "#000000" };
    if (key === "withpo") return { bg: "#DCFCE7", fg: "#000000" };
    if (key === "component") return { bg: "#FEF3C7", fg: "#000000" };
    if (key === "analysis") return { bg: "#DBEAFE", fg: "#000000" };
    if (key === "pickup") return { bg: "#FEE2E2", fg: "#000000" };
    if (key === "ready") return { bg: "#D1FAE5", fg: "#000000" };

    return { bg: "#F3F4F6", fg: "#000000" };
  };

  const statusMatchesChip = useCallback(
    (statusText: string) => {
      if (statusChip === "all") return true;
      return statusKeyOf(statusText) === statusChip;
    },
    [statusChip]
  );

  const chipCounts = useMemo(() => {
    const counts: Record<ChipKey, number> = {
      all: rows.length,
      po: 0,
      withpo: 0,
      component: 0,
      analysis: 0,
      pickup: 0,
      ready: 0,
    };

    if (statusColIndex == null) return counts;

    for (const r of rows) {
      const txt = getStatusText(r[statusColIndex]);
      const key = statusKeyOf(txt);
      if (key !== "other") counts[key] += 1;
    }

    return counts;
  }, [rows, statusColIndex]);

  const companyOptions = useMemo(() => {
    if (effectiveCompanyColIndex == null) return [];

    const map = new Map<string, { label: string; count: number }>();

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      }

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (
          (financeFilter === "pending" && financeState !== "pending_payment") ||
          (financeFilter === "paid" && financeState !== "paid")
        ) {
          return;
        }
      }

      const company = cellToPlainText(row[effectiveCompanyColIndex]).trim();
      if (!company) return;

      const curr = map.get(company);
      if (curr) curr.count += 1;
      else map.set(company, { label: company, count: 1 });
    });

    return Array.from(map.entries())
      .map(([value, meta]) => ({
        value,
        label: meta.label,
        count: meta.count,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [
    effectiveCompanyColIndex,
    rows,
    statusColIndex,
    statusMatchesChip,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const personOptions = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      }

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (
          (financeFilter === "pending" && financeState !== "pending_payment") ||
          (financeFilter === "paid" && financeState !== "paid")
        ) {
          return;
        }
      }

      if (companyFilter !== "__all__" && effectiveCompanyColIndex != null) {
        const companyTxt = cellToPlainText(
          row[effectiveCompanyColIndex]
        ).trim();
        if (companyTxt !== companyFilter) return;
      }

      const names = new Set<string>();

      if (solicitanteColIndex != null) {
        const solicitante = cellToPlainText(row[solicitanteColIndex]).trim();
        if (solicitante) names.add(solicitante);
      }

      if (copiaColIndex != null) {
        splitNamesForOverlay(cellToPlainText(row[copiaColIndex])).forEach(
          (n) => {
            if (n) names.add(n);
          }
        );
      }

      Array.from(names).forEach((name) => {
        const existing = map.get(name);
        if (existing) existing.count += 1;
        else map.set(name, { label: name, count: 1 });
      });
    });

    return Array.from(map.entries())
      .map(([value, meta]) => ({
        value,
        label: meta.label,
        count: meta.count,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [
    rows,
    statusColIndex,
    statusMatchesChip,
    companyFilter,
    effectiveCompanyColIndex,
    solicitanteColIndex,
    copiaColIndex,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const rowMatchesPersonFilter = useCallback(
    (row: (string | number | React.ReactNode)[]) => {
      if (personFilter === "__all__") return true;

      const target = normalizeName(personFilter);
      if (!target) return true;

      let solicitanteMatch = false;
      let copiaMatch = false;

      if (solicitanteColIndex != null) {
        const solicitante = normalizeName(
          cellToPlainText(row[solicitanteColIndex])
        );
        solicitanteMatch = solicitante === target;
      }

      if (copiaColIndex != null) {
        const copiaNames = splitNamesForOverlay(
          cellToPlainText(row[copiaColIndex])
        ).map((n) => normalizeName(n));
        copiaMatch = copiaNames.includes(target);
      }

      return solicitanteMatch || copiaMatch;
    },
    [personFilter, solicitanteColIndex, copiaColIndex]
  );

  const { visibleRows, viewToOriginal } = useMemo(() => {
    const filtered: {
      row: (string | number | React.ReactNode)[];
      original: number;
    }[] = [];

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      } else {
        if (statusChip !== "all") return;
      }

      if (effectiveCompanyColIndex != null && companyFilter !== "__all__") {
        const companyTxt = cellToPlainText(
          row[effectiveCompanyColIndex]
        ).trim();
        if (companyTxt !== companyFilter) return;
      }

      if (!rowMatchesPersonFilter(row)) return;

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (financeFilter === "pending" && financeState !== "pending_payment")
          return;
        if (financeFilter === "paid" && financeState !== "paid") return;
      }

      const passes = row.every((cell, i) => {
        const f = (filters[i] ?? "").toLowerCase().trim();
        if (!f) return true;

        const v = cellToPlainText(cell).toLowerCase();
        return v.includes(f);
      });

      if (passes) filtered.push({ row, original: originalIdx });
    });

    const statusPriority: Record<string, number> = {
      po: 0,
      withpo: 1,
      component: 2,
      analysis: 3,
      pickup: 4,
      ready: 5,
      other: 9,
    };

    const priorityOfRow = (r: (string | number | React.ReactNode)[]) => {
      if (statusColIndex == null) return statusPriority.other;
      const txt = getStatusText(r[statusColIndex]);
      const key = statusKeyOf(txt);
      return statusPriority[key] ?? statusPriority.other;
    };

    const doSortByColumn = (a: any, b: any) => {
      if (sort.index == null) return 0;

      const i = sort.index;
      const m = sort.direction === "asc" ? 1 : -1;

      const A = getComparable(a.row[i]);
      const B = getComparable(b.row[i]);

      if (A.kind === "num" && B.kind === "num") {
        if (A.val < B.val) return -1 * m;
        if (A.val > B.val) return 1 * m;
        return 0;
      }

      return (
        String(A.val).localeCompare(String(B.val), "pt-BR", {
          sensitivity: "base",
          numeric: true,
        }) * m
      );
    };

    filtered.sort((a, b) => {
      const byCol = doSortByColumn(a, b);
      if (byCol !== 0) return byCol;

      const ap = priorityOfRow(a.row);
      const bp = priorityOfRow(b.row);
      if (ap !== bp) return ap - bp;

      return a.original - b.original;
    });

    return {
      visibleRows: filtered.map((x) => x.row),
      viewToOriginal: filtered.map((x) => x.original),
    };
  }, [
    rows,
    filters,
    sort,
    statusColIndex,
    statusChip,
    statusMatchesChip,
    effectiveCompanyColIndex,
    companyFilter,
    rowMatchesPersonFilter,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const selectRow = useCallback(
    (index: number | null) => {
      if (index == null || index < 0 || index >= visibleRows.length) return;

      const linha = visibleRows[index];

      if (isTableStandby) {
        const raw = linha[osColumnIndex];
        const osValue = extractOS(raw);
        setSelectedOS(osValue || null);
        setSelectedViewIndex(null);
      } else {
        setSelectedViewIndex(index);
        setSelectedOS(null);
      }

      const root = tableRef.current;
      if (root) {
        const trs = root.querySelectorAll("tbody tr[data-row='true']");
        if (trs[index]) {
          (trs[index] as HTMLElement).scrollIntoView({ block: "nearest" });
        }
      }
    },
    [visibleRows, isTableStandby, osColumnIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();

      if (!visibleRows.length) return;

      let current =
        isTableStandby && selectedOS
          ? visibleRows.findIndex(
              (r) => extractOS(r[osColumnIndex]) === selectedOS
            )
          : selectedViewIndex ?? -1;

      if (e.key === "ArrowDown") {
        current = Math.min(visibleRows.length - 1, current + 1);
      } else {
        current = Math.max(0, current - 1);
      }

      selectRow(current);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visibleRows,
    selectedOS,
    selectedViewIndex,
    selectRow,
    isTableStandby,
    osColumnIndex,
  ]);

  const colorForDelay = (value: string | number | React.ReactNode) => {
    const txt = cellToPlainText(value);

    let n: number | null = null;
    if (typeof value === "number") n = Number.isFinite(value) ? value : null;
    else if (typeof value === "string") n = parseMaybeNumber(value);
    else n = parseMaybeNumber(txt);

    if (n == null) return null;

    let bg = atrasoBad;
    if (n <= 3) bg = atrasoGood;
    else if (n <= 9) bg = atrasoWarn;

    const fg = bg === atrasoBad ? "#fff" : "#111";
    return { bg, fg, n };
  };

  const looksLikeYellow = (c?: string) => {
    if (!c) return false;
    const x = c.toLowerCase().replace(/\s+/g, "");
    return (
      x.includes("#fff9d6") ||
      x.includes("#fffac8") ||
      x.includes("ffe79a") ||
      x.includes("fff7ed") ||
      x.includes("fffbeb")
    );
  };

  const isPendingRowFn = (
    linha: (string | number | React.ReactNode)[],
    vIndex: number,
    oIndex: number
  ) => {
    if (isRowPending) return !!isRowPending(linha, vIndex, oIndex);
    if ((linha as any)?.__pendente_retorno === true) return true;

    if (enableCellColors && getCellStyle) {
      for (let col = 0; col < headers.length; col++) {
        const extra =
          getCellStyle(vIndex, col, linha[col], oIndex, linha, headers[col]) ||
          {};
        const bg =
          (extra.backgroundColor as string) || (extra.background as string);
        if (looksLikeYellow(bg)) return true;
      }
    }

    return false;
  };

  const moveOverlay = useCallback(
    (clientX: number, clientY: number, text: string) => {
      if (overlayRafRef.current != null) {
        cancelAnimationFrame(overlayRafRef.current);
      }

      overlayRafRef.current = requestAnimationFrame(() => {
        setOverlay({
          visible: true,
          x: clientX + 14,
          y: clientY + 18,
          text,
        });
      });
    },
    []
  );

  const hideOverlay = useCallback(() => {
    if (overlayRafRef.current != null) {
      cancelAnimationFrame(overlayRafRef.current);
      overlayRafRef.current = null;
    }

    setOverlay((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const activeColSpan = useMemo(
    () =>
      headers.reduce(
        (acc, _h, i) => (activeWidths[i] === "0px" ? acc : acc + 1),
        0
      ),
    [headers, activeWidths]
  );

  const getStableRowKey = (viewIndex: number, originalIndex: number) => {
    const rid =
      rowIds && typeof originalIndex === "number"
        ? rowIds[originalIndex]
        : null;

    if (rid !== null && rid !== undefined && String(rid).trim() !== "") {
      return `row-${String(rid)}`;
    }

    return `row-${originalIndex}-${viewIndex}`;
  };

  const footerTotals = useMemo(() => {
    return headers.map((_, colIndex) => {
      let hasNumeric = false;
      let total = 0;

      visibleRows.forEach((row) => {
        const cell = row[colIndex];
        if (typeof cell === "number") {
          total += cell;
          hasNumeric = true;
          return;
        }
        if (typeof cell === "string") {
          const n = parseMaybeNumber(cell);
          if (n !== null) {
            total += n;
            hasNumeric = true;
          }
        }
      });

      if (!hasNumeric) return "";
      return total.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    });
  }, [headers, visibleRows]);

  const chipWrapStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "10px 0 12px",
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    height: 32,
    padding: "0 12px",
    borderRadius: 999,
    border: active ? `1px solid ${baseSelectionColor}` : "1px solid #cbd5e1",
    background: active ? baseSelectionColor : "#ffffff",
    color: active ? "#ffffff" : "#111827",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    userSelect: "none",
    transition: "all 0.15s ease",
    boxShadow: active ? "0 6px 16px rgba(0,0,0,0.12)" : "none",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  });

  const chipDot = (bg: string): React.CSSProperties => ({
    width: 10,
    height: 10,
    borderRadius: 999,
    background: bg,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.25) inset",
    flexShrink: 0,
  });

  const chipCountPill: React.CSSProperties = {
    marginLeft: 2,
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.22)",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    flexShrink: 0,
  };

  const filterBarStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    padding: "0 0 12px",
  };

  const filterBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 260,
    flex: "0 1 320px",
  };

  const filterLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#374151",
    fontWeight: 500,
  };

  const selectStyle: React.CSSProperties = {
    height: 36,
    borderRadius: 0,
    border: "1px solid #cbd5e1",
    padding: "0 10px",
    fontSize: 14,
    outline: "none",
    background: "#ffffff",
    color: "#111827",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: filterHeight,
    border: "1px solid #cbd5e1",
    outline: "none",
    padding: "0 8px",
    fontSize: 13,
    background: filterBg,
    boxSizing: "border-box",
  };

  const styles: Record<string, React.CSSProperties> = {
    wrapper: {
      width: "100%",
      height: "100%",
      overflow: "hidden",
      borderRadius: "0px",
      border: `1px solid ${borderColor}`,
      background: "#ffffff",
      display: "flex",
      flexDirection: "column",
    },
    scroller: {
      width: "100%",
      height: "100%",
      overflow: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    td: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      boxSizing: "border-box",
    },
  };

  const ChipLabel: React.FC<{ label: string; count: number }> = ({
    label,
    count,
  }) => (
    <>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-block",
          maxWidth: "100%",
        }}
        title={label}
      >
        {label}
      </span>
      <span style={chipCountPill}>{count}</span>
    </>
  );

  const handleHeaderSort = (colIndex: number) => {
    if (activeWidths[colIndex] === "0px") return;

    setSort((prev) => {
      if (prev.index !== colIndex) {
        return { index: colIndex, direction: "asc" };
      }

      return {
        index: colIndex,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });

    setSelectedOS(null);
    setSelectedViewIndex(null);
  };

  const renderSortIndicator = (colIndex: number) => {
    if (sort.index !== colIndex) {
      return <span style={{ opacity: 0.35, fontSize: 11 }}>↕</span>;
    }

    return (
      <span style={{ fontSize: 11 }}>
        {sort.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const getGroupMeta = (
    row: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => {
    if (!getRowGroup) {
      return { groupKey: null as string | null, showHeader: false };
    }

    const groupKey = getRowGroup(row, viewIndex, originalIndex);
    if (!groupKey) {
      return { groupKey: null as string | null, showHeader: false };
    }

    if (viewIndex === 0) {
      return { groupKey, showHeader: true };
    }

    const prevOriginal = viewToOriginal[viewIndex - 1];
    const prevRow = visibleRows[viewIndex - 1];
    const prevGroupKey = getRowGroup(prevRow, viewIndex - 1, prevOriginal);

    return {
      groupKey,
      showHeader: prevGroupKey !== groupKey,
    };
  };

  const overlayNode =
    typeof document !== "undefined" && overlay.visible
      ? createPortal(
          <div
            style={{
              position: "fixed",
              left: overlay.x,
              top: overlay.y,
              zIndex: 999999,
              pointerEvents: "none",
              maxWidth: 360,
              background: "#111827",
              color: "#ffffff",
              padding: "8px 10px",
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.4,
              boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
              whiteSpace: "pre-wrap",
            }}
          >
            {overlay.text}
          </div>,
          document.body
        )
      : null;

  return (
    <div style={styles.wrapper}>
      {(statusColIndex != null || effectiveCompanyColIndex != null) && (
        <div style={{ padding: "10px 12px 0" }}>
          {statusColIndex != null && (
            <div style={chipWrapStyle}>
              <button
                type="button"
                style={chipStyle(statusChip === "all")}
                onClick={() => setStatusChip("all")}
              >
                <span style={chipDot("#9CA3AF")} />
                <ChipLabel label="Todos" count={chipCounts.all} />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "po")}
                onClick={() => setStatusChip("po")}
              >
                <span style={chipDot("#FDE7C7")} />
                <ChipLabel label="Aguardando P.O." count={chipCounts.po} />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "withpo")}
                onClick={() => setStatusChip("withpo")}
              >
                <span style={chipDot("#DCFCE7")} />
                <ChipLabel label="Com P.O." count={chipCounts.withpo} />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "component")}
                onClick={() => setStatusChip("component")}
              >
                <span style={chipDot("#FEF3C7")} />
                <ChipLabel
                  label="Aguardando componente"
                  count={chipCounts.component}
                />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "analysis")}
                onClick={() => setStatusChip("analysis")}
              >
                <span style={chipDot("#DBEAFE")} />
                <ChipLabel label="Em análise" count={chipCounts.analysis} />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "pickup")}
                onClick={() => setStatusChip("pickup")}
              >
                <span style={chipDot("#FEE2E2")} />
                <ChipLabel
                  label="Aguardando retirada"
                  count={chipCounts.pickup}
                />
              </button>

              <button
                type="button"
                style={chipStyle(statusChip === "ready")}
                onClick={() => setStatusChip("ready")}
              >
                <span style={chipDot("#D1FAE5")} />
                <ChipLabel
                  label="Reparado / operacional"
                  count={chipCounts.ready}
                />
              </button>
            </div>
          )}

          <div style={filterBarStyle}>
            {effectiveCompanyColIndex != null && (
              <div style={filterBlockStyle}>
                <label style={filterLabelStyle}>Empresa</label>
                <select
                  style={selectStyle}
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <option value="__all__">Todas</option>
                  {companyOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label} ({item.count})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(solicitanteColIndex != null || copiaColIndex != null) && (
              <div style={filterBlockStyle}>
                <label style={filterLabelStyle}>Pessoa</label>
                <select
                  style={selectStyle}
                  value={personFilter}
                  onChange={(e) => setPersonFilter(e.target.value)}
                >
                  <option value="__all__">Todas</option>
                  {personOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label} ({item.count})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showFinanceFilter && (
              <div style={filterBlockStyle}>
                <label style={filterLabelStyle}>Financeiro</label>
                <select
                  style={selectStyle}
                  value={financeFilter}
                  onChange={(e) =>
                    setFinanceFilter(e.target.value as FinanceFilter)
                  }
                >
                  <option value="__all__">Todos</option>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      <div ref={tableRef} style={styles.scroller}>
        <table style={styles.table}>
          <thead>
            <tr>
              {headers.map((headerTitle, colIndex) => {
                if (activeWidths[colIndex] === "0px") return null;

                const isSticky =
                  stickyFirstCol && colIndex === firstVisibleColIndex;

                return (
                  <th
                    key={`h-${colIndex}`}
                    onClick={() => handleHeaderSort(colIndex)}
                    style={{
                      background: headerBg,
                      color: headerText,
                      border: `0.6px solid ${borderColor}`,
                      fontSize: "13px",
                      fontWeight: 600,
                      height: headerHeight,
                      padding: "0 8px",
                      letterSpacing: "0.2px",
                      cursor: "pointer",
                      userSelect: "none",
                      position: isSticky ? "sticky" : undefined,
                      left: isSticky ? 0 : undefined,
                      zIndex: isSticky ? 4 : 2,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={headerTitle}
                      >
                        {headerTitle}
                      </span>
                      {renderSortIndicator(colIndex)}
                    </div>
                  </th>
                );
              })}
            </tr>

            <tr>
              {headers.map((headerTitle, colIndex) => {
                if (activeWidths[colIndex] === "0px") return null;

                const isSticky =
                  stickyFirstCol && colIndex === firstVisibleColIndex;

                return (
                  <th
                    key={`f-${colIndex}`}
                    style={{
                      background: filterBg,
                      border: `0.6px solid ${borderColor}`,
                      height: filterHeight,
                      padding: 0,
                      position: isSticky ? "sticky" : undefined,
                      left: isSticky ? 0 : undefined,
                      zIndex: isSticky ? 3 : 1,
                    }}
                  >
                    <input
                      value={filters[colIndex] ?? ""}
                      onChange={(e) => {
                        const next = [...filters];
                        next[colIndex] = e.target.value;
                        updateFilters(next);
                      }}
                      placeholder={`Filtrar ${headerTitle}`}
                      style={inputStyle}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={Math.max(activeColSpan, 1)}
                  style={{
                    border: `0.6px solid ${borderColor}`,
                    padding: "16px",
                    textAlign: "center",
                    color: "#6B7280",
                    fontSize: 13,
                    background: "#ffffff",
                  }}
                >
                  Nenhum registro encontrado.
                </td>
              </tr>
            )}

            {visibleRows.map((linha, viewIndex) => {
              const originalIndex = viewToOriginal[viewIndex];
              const rowKey = getStableRowKey(viewIndex, originalIndex);

              const { groupKey, showHeader } = getGroupMeta(
                linha,
                viewIndex,
                originalIndex
              );

              const isSelected = isTableStandby
                ? extractOS(linha[osColumnIndex]) === selectedOS
                : selectedViewIndex === viewIndex;

              const isPending = isPendingRowFn(linha, viewIndex, originalIndex);

              const rowBackground = isPending
                ? isSelected
                  ? PENDING_SELECTED_SOLID
                  : PENDING_BASE
                : isSelected
                ? optionSelected
                : hoverRow === viewIndex && hoverHighlight
                ? ROW_HOVER_BG
                : rowBaseBg(viewIndex);

              const extraRowStyle =
                getRowStyle?.(linha, viewIndex, originalIndex) || {};

              return (
                <React.Fragment key={rowKey}>
                  {showHeader && groupKey && (
                    <tr>
                      <td
                        colSpan={Math.max(activeColSpan, 1)}
                        style={{
                          background: groupBarBg,
                          color: groupBarText,
                          border: `0.6px solid ${borderColor}`,
                          padding: "7px 10px",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                        }}
                      >
                        {groupLabels?.[groupKey] ?? groupKey}
                      </td>
                    </tr>
                  )}

                  <tr
                    data-row="true"
                    onMouseEnter={() => setHoverRow(viewIndex)}
                    onMouseLeave={() => {
                      setHoverRow((prev) => (prev === viewIndex ? null : prev));
                      hideOverlay();
                    }}
                    onClick={() => selectRow(viewIndex)}
                    onDoubleClick={() =>
                      onDoubleClick?.(viewIndex, linha, originalIndex)
                    }
                    style={{
                      cursor: onDoubleClick ? "pointer" : "default",
                      ...extraRowStyle,
                    }}
                  >
                    {headers.map((headerTitle, colIndex) => {
                      if (activeWidths[colIndex] === "0px") return null;

                      const cell = linha[colIndex];
                      const plainText = cellToPlainText(cell);

                      const extra =
                        enableCellColors && getCellStyle
                          ? getCellStyle(
                              viewIndex,
                              colIndex,
                              cell,
                              originalIndex,
                              linha,
                              headerTitle
                            ) || {}
                          : {};

                      const delayInfo =
                        typeof atrasoColumnIndex === "number" &&
                        atrasoColumnIndex === colIndex
                          ? colorForDelay(cell)
                          : null;

                      const statusInfo =
                        statusColIndex === colIndex
                          ? statusStyle(plainText)
                          : null;

                      const financeInfo =
                        showFinanceFilter &&
                        normalizeText(headerTitle).includes("finance")
                          ? FINANCE_COLORS[
                              financeMap[originalIndex] ?? "pending_payment"
                            ]
                          : null;

                      const isSticky =
                        stickyFirstCol && colIndex === firstVisibleColIndex;

                      let finalBg: string | undefined = undefined;
                      let finalColor: string | undefined = undefined;

                      if (!isSelected && hoverRow !== viewIndex) {
                        if (delayScope === "row" && delayInfo) {
                          finalBg = delayInfo.bg;
                          finalColor = delayInfo.fg;
                        } else if (delayScope === "cell" && delayInfo) {
                          finalBg = delayInfo.bg;
                          finalColor = delayInfo.fg;
                        } else if (statusInfo) {
                          finalBg = statusInfo.bg;
                          finalColor = statusInfo.fg;
                        } else if (financeInfo) {
                          finalBg = financeInfo;
                          finalColor = "#ffffff";
                        } else if (extra.backgroundColor) {
                          finalBg = String(extra.backgroundColor);
                        } else if (extra.background) {
                          finalBg = String(extra.background);
                        }
                      }

                      const titleText =
                        copiaColIndex === colIndex
                          ? splitNamesForOverlay(plainText).join("\n")
                          : plainText;

                      const shouldOverlay =
                        copiaColIndex === colIndex &&
                        splitNamesForOverlay(plainText).length > 0;

                      return (
                        <td
                          key={`${rowKey}-${colIndex}`}
                          onMouseMove={(e) => {
                            if (shouldOverlay) {
                              moveOverlay(e.clientX, e.clientY, titleText);
                            }
                          }}
                          onMouseLeave={() => {
                            if (shouldOverlay) hideOverlay();
                          }}
                          title={!shouldOverlay ? plainText : undefined}
                          style={{
                            ...styles.td,
                            width: activeWidths[colIndex],
                            height: cellHeight,
                            padding: cellPadding,
                            fontSize,
                            border: `0.6px solid ${borderColor}`,
                            background: finalBg ?? rowBackground,
                            color:
                              isSelected && selectedTextWhite
                                ? "#ffffff"
                                : finalColor ??
                                  (isSelected ? "#111111" : "#111111"),
                            fontWeight: isSelected ? 600 : 500,
                            transition: "all 0.18s ease",
                            position: isSticky ? "sticky" : undefined,
                            left: isSticky ? 0 : undefined,
                            zIndex: isSticky ? 2 : 1,
                            boxShadow: isSticky
                              ? `1px 0 0 ${borderColor}`
                              : undefined,
                            ...(extra || {}),
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {overlayNode}
    </div>
  );
};

export default Table;
